import React, { useEffect, useState } from "react";
import { Fragment } from "react";
import Select from "react-select";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import {
  getQuotations
} from "../../../actions/admin/quotation";
import Item from "./Item";
import AdminPage from "../_components/AdminPage";
import AdminCreateButton from "../_components/AdminCreateButton";
import AdminTable from "../_components/AdminTable";
import AdminFilterBar, {
  adminSelectStyles,
  DateRangeField,
  MultiSelectField,
} from "../_components/AdminFilterBar";
import { SelectDropdownIndicator } from "../_components/Icons";

const TABLE_COLUMNS = [
  { key: "no", title: "#", width: "tw:w-[60px] tw:rounded-tl-[5px]", skeleton: "narrow" },
  { key: "client", title: "Client Name", width: "tw:w-[220px]" },
  { key: "quotationNo", title: "Quotation No.", width: "tw:w-[180px]" },
  { key: "amount", title: "Amount", width: "tw:w-[220px]" },
  { key: "issueDate", title: "Issue Date", width: "tw:w-[180px]" },
  { key: "validUntil", title: "Valid Until", width: "tw:w-[180px]" },
  { key: "status", title: "Status", width: "tw:w-[220px]", skeleton: "badge" },
  { key: "action", title: "Action", width: "tw:w-[190px] tw:rounded-tr-[5px]", sticky: true, align: "center" },
];

const SORT_OPTIONS = [
  { label: "Quotation No.", value: "id" },
  { label: "Client Name", value: "name" },
  { label: "Issue Date", value: "date" },
  { label: "Updated Date", value: "updatedDate" },
];

// UNSAVED = created but never saved, so it has no Quotation No. yet. Leaving
// every option unselected lists every status, UNSAVED included.
const QUOTATION_STATUS_OPTIONS = [
  { label: "Unsaved", value: "UNSAVED" },
  { label: "Draft", value: "DRAFT" },
  { label: "Active", value: "ACTIVE" },
  { label: "Expired", value: "EXPIRED" },
];

// Labels match the status badge in Item.js.
const PAYMENT_STATUS_OPTIONS = [
  { label: "Not Paid", value: "NOT_PAID" },
  { label: "Ready To Charge", value: "READY_TO_CHARGE" },
  { label: "Charge Processing", value: "CHARGE_INITIATED" },
  { label: "Charged", value: "CHARGED" },
  { label: "Charge Failed", value: "CHARGE_FAILED" },
  { label: "Paid (legacy)", value: "PAID" },
];

// Filters live in the URL as plain strings so a filtered list can be
// bookmarked / reloaded: statuses comma-separated, dates as YYYY-MM-DD.
const FILTER_KEYS = [
  "status",
  "paymentStatus",
  "issueDateFrom",
  "issueDateTo",
  "expiredDateFrom",
  "expiredDateTo",
];

const EMPTY_FILTERS = FILTER_KEYS.reduce((filters, key) => ({ ...filters, [key]: "" }), {});

const URL_DATE_FORMAT = "YYYY-MM-DD";

const readParamsFromUrl = (search) => {
  const queryParams = new URLSearchParams(search);
  const filters = FILTER_KEYS.reduce(
    (result, key) => ({ ...result, [key]: queryParams.get(key) || "" }),
    {},
  );

  return {
    search: queryParams.get("search") || "",
    page: parseInt(queryParams.get("page")) || 1,
    limit: parseInt(queryParams.get("limit")) || 10,
    orderBy: queryParams.get("orderBy") || "updatedDate",
    sortBy: queryParams.get("sortBy") || "desc",
    ...filters,
  };
};

// Only keep params that have a value, so the URL stays short and readable.
const buildUrlSearch = (params) => {
  const nonEmptyEntries = Object.entries(params).filter(([, value]) => value !== "" && value != null);
  return new URLSearchParams(nonEmptyEntries).toString();
};

const isSameParams = (a, b) => Object.keys(a).every((key) => a[key] === b[key]);

// "ACTIVE,DRAFT" -> the matching react-select options.
const csvToOptions = (csv, options) => {
  const values = csv ? csv.split(",") : [];
  return options.filter((option) => values.includes(option.value));
};

const optionsToCsv = (selectedOptions) => (selectedOptions || []).map((option) => option.value).join(",");

// URL dates -> the { startDate, endDate } shape DateRangeField expects.
const toDateRange = (fromDate, toDate) =>
  fromDate && toDate
    ? { startDate: moment(fromDate, URL_DATE_FORMAT), endDate: moment(toDate, URL_DATE_FORMAT) }
    : {};

// The admin picks calendar days, so format in local time (no UTC shift).
const formatUrlDate = (date) => (date ? moment(date).format(URL_DATE_FORMAT) : "");

const pickFilters = (params) =>
  FILTER_KEYS.reduce((filters, key) => ({ ...filters, [key]: params[key] }), {});

const Page = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const quotation = useSelector((state) => state.quotation)
  const { loading, quotations } = quotation;
  const [params, setParams] = useState(() => readParamsFromUrl(location.search));
  // What the admin is editing in the filter panel; only copied into `params`
  // (and the URL) when they press Apply.
  const [draftFilters, setDraftFilters] = useState(() => pickFilters(readParamsFromUrl(location.search)));
  // Bumped on Clear so the date pickers remount and drop their old selection.
  const [dateFieldsResetKey, setDateFieldsResetKey] = useState(0);

  useEffect(() => {
    const handler = setTimeout(() => {
      dispatch(getQuotations(params));
    }, 500); // debounce delay in ms (adjust as needed)

    return () => {
      clearTimeout(handler);
    };
  }, [dispatch, params]);

  // Browser back/forward changes the URL without going through our handlers,
  // so re-read it and follow along.
  useEffect(() => {
    const paramsFromUrl = readParamsFromUrl(location.search);
    if (!isSameParams(paramsFromUrl, params)) {
      setParams(paramsFromUrl);
      setDraftFilters(pickFilters(paramsFromUrl));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const updateParams = (changes) => {
    const updatedParams = { ...params, ...changes };
    setParams(updatedParams);
    history.push({ search: buildUrlSearch(updatedParams) });
  };

  const handleChangeOrderBy = (selectedOption) => updateParams({ orderBy: selectedOption.value });

  // A new search or filter can have fewer pages, so always go back to page 1.
  const handleChangeSearch = (e) => updateParams({ search: e.target.value, page: 1 });

  const handleChange = (key, value) => updateParams({ [key]: value });

  const updateDraftFilter = (changes) => setDraftFilters((current) => ({ ...current, ...changes }));

  const handleApplyFilters = () => updateParams({ ...draftFilters, page: 1 });

  const handleClearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setDateFieldsResetKey((key) => key + 1);
    updateParams({ ...EMPTY_FILTERS, page: 1 });
  };

  return (
    <AdminPage
      title="Quotation List"
      actions={<AdminCreateButton to="/admin/quotations/create" label="Create a Quotation" />}
    >
      <AdminFilterBar
        search={params.search}
        setSearch={(value) => handleChangeSearch({ target: { value } })}
        searchPlaceholder="Search"
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        defaultOpen={FILTER_KEYS.some((key) => params[key])}
        trailing={
          <div className="tw:flex tw:items-center tw:gap-2">
            <span className="tw:text-[14px] tw:leading-5 tw:!text-[#7C7B7B]">Sort By</span>
            <div className="tw:w-[220px]">
              <Select
                styles={adminSelectStyles}
                options={SORT_OPTIONS}
                onChange={handleChangeOrderBy}
                value={SORT_OPTIONS.find((o) => o.value === params.orderBy) || SORT_OPTIONS[3]}
                isSearchable={false}
                components={{ DropdownIndicator: SelectDropdownIndicator, IndicatorSeparator: null }}
              />
            </div>
          </div>
        }
      >
        <MultiSelectField
          label="Quotation Status"
          placeholder="All statuses"
          options={QUOTATION_STATUS_OPTIONS}
          value={csvToOptions(draftFilters.status, QUOTATION_STATUS_OPTIONS)}
          onChange={(selected) => updateDraftFilter({ status: optionsToCsv(selected) })}
        />
        <MultiSelectField
          label="Payment Status"
          placeholder="All payment statuses"
          options={PAYMENT_STATUS_OPTIONS}
          value={csvToOptions(draftFilters.paymentStatus, PAYMENT_STATUS_OPTIONS)}
          onChange={(selected) => updateDraftFilter({ paymentStatus: optionsToCsv(selected) })}
        />
        <DateRangeField
          key={`issue-date-${dateFieldsResetKey}`}
          label="Issue Date"
          placeholder="Any date"
          dateRange={toDateRange(draftFilters.issueDateFrom, draftFilters.issueDateTo)}
          onChange={({ startDate, endDate }) =>
            updateDraftFilter({ issueDateFrom: formatUrlDate(startDate), issueDateTo: formatUrlDate(endDate) })
          }
        />
        <DateRangeField
          key={`valid-until-${dateFieldsResetKey}`}
          label="Valid Until"
          placeholder="Any date"
          dateRange={toDateRange(draftFilters.expiredDateFrom, draftFilters.expiredDateTo)}
          onChange={({ startDate, endDate }) =>
            updateDraftFilter({ expiredDateFrom: formatUrlDate(startDate), expiredDateTo: formatUrlDate(endDate) })
          }
        />
      </AdminFilterBar>

      <Fragment>
        <AdminTable
          columns={TABLE_COLUMNS}
          minWidth={1450}
          loading={loading}
          skeletonRowCount={params.limit}
          isEmpty={!quotations?.data?.length}
          emptyText="No quotations found."
        >
          {quotations?.data?.map((p, i = 0) => (
            <Item key={p._id} quotation={p} count={i++} />
          ))}
        </AdminTable>
            {
              quotations && (() => {
                const currentPage = Number(params.page);
                const totalPages = quotations.hasMore
                  ? Math.ceil(quotations.recordsTotal / quotations.recordsFiltered)
                  : Math.ceil(quotations.recordsTotal / params.limit);

                const windowStart = Math.max(2, currentPage - 2);
                const windowEnd = Math.min(totalPages - 1, currentPage + 2);

                const pageItems = [];

                // First page
                pageItems.push(
                  <li key={1} className={`page-item ${currentPage === 1 ? "active" : "pointer"}`} onClick={() => handleChange('page', 1)}>
                    <a className="page-link text-dark">{1}</a>
                  </li>
                );

                // Left ellipsis
                if (windowStart > 2) {
                  pageItems.push(
                    <li key="ellipsis-left" className="page-item disabled">
                      <a className="page-link text-dark">…</a>
                    </li>
                  );
                }

                // Window pages
                for (let p = windowStart; p <= windowEnd; p++) {
                  pageItems.push(
                    <li key={p} className={`page-item ${currentPage === p ? "active" : "pointer"}`} onClick={() => handleChange('page', p)}>
                      <a className="page-link text-dark">{p}</a>
                    </li>
                  );
                }

                // Right ellipsis
                if (windowEnd < totalPages - 1) {
                  pageItems.push(
                    <li key="ellipsis-right" className="page-item disabled">
                      <a className="page-link text-dark">…</a>
                    </li>
                  );
                }

                // Last page
                if (totalPages > 1) {
                  pageItems.push(
                    <li key={totalPages} className={`page-item ${currentPage === totalPages ? "active" : "pointer"}`} onClick={() => handleChange('page', totalPages)}>
                      <a className="page-link text-dark">{totalPages}</a>
                    </li>
                  );
                }

                return (
                  <nav>
                    <ul className="pagination">
                      <li
                        className={`page-item ${currentPage === 1 ? "disabled" : "pointer"}`}
                        onClick={() => { if (currentPage > 1) handleChange("page", currentPage - 1); }}
                      >
                        <a className="page-link text-dark" aria-label="Previous">
                          <span aria-hidden="true">&laquo;</span>
                        </a>
                      </li>
                      {pageItems}
                      <li
                        className={`page-item ${!quotations.hasMore ? "disabled" : "pointer"}`}
                        onClick={() => { if (quotations.hasMore) handleChange("page", currentPage + 1); }}
                      >
                        <a className="page-link text-dark" aria-label="Next">
                          <span aria-hidden="true">&raquo;</span>
                        </a>
                      </li>
                    </ul>
                  </nav>
                );
              })()
            }

      </Fragment>
    </AdminPage>
  );
};

export default Page;
